import { api, buildQueryParams, URLs } from '@/libs/api'
import type {
  ApiResponse,
  CreateApiResult,
  CreateUserEmployeePayload,
  CursorPaginatedResponse,
  DeleteEmployeeRegularHoursPayload,
  Employee,
  EmployeeData,
  EmployeeListParams,
  EmployeeRegularHour,
  EmployeeRegularHoursParams,
  EmployeeUser,
  SaveEmployeeRegularHoursPayload,
  UpdateEmployeeRegularHoursPayload,
} from '@/libs/interfaces'
import { employeesApiMock } from '@/libs/mocks/handlers/employees'

// The backend's DELETE .../regular-hours takes `weekDays` as a repeated
// querystring param (?weekDays=monday&weekDays=tuesday), which
// buildQueryParams (scalar-only) can't express.
const buildWeekDaysQuery = (weekDays: string[]): string => {
  const searchParams = new URLSearchParams()
  weekDays.forEach((weekDay) => searchParams.append('weekDays', weekDay))
  const query = searchParams.toString()
  return query ? `?${query}` : ''
}

const employeesApiReal = {
  create: async (body: Partial<Employee>): Promise<CreateApiResult<Partial<Employee>>> => {
    return await api
      .post<ApiResponse<Partial<Employee>>>(URLs.Employees.create(body.orgID || ''), body)
      .then((response) => ({
        data: response.data.data,
        status: response.status,
        message: response.message,
      }))
  },
  getByID: async (params: { orgID?: string; employeeID?: string }): Promise<Partial<Employee>> => {
    const { orgID, employeeID } = params
    return await api
      .get<ApiResponse<Partial<Employee>>>(URLs.Employees.byID(orgID ?? '', employeeID ?? ''))
      .then((response) => response.data.data)
  },
  getFromUserID: async (orgID: string, userID: string): Promise<EmployeeUser> => {
    return await api
      .get<ApiResponse<EmployeeUser>>(URLs.Employees.byUserID(orgID, userID))
      .then((response) => response.data.data)
  },
  list: async (params: EmployeeListParams): Promise<Employee[]> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    return await api
      .get<ApiResponse<EmployeeData>>(URLs.Employees.list(storeID, query))
      .then((response) => response.data?.data.employees || [])
  },
  listCursor: async (params: EmployeeListParams): Promise<CursorPaginatedResponse<Employee>> => {
    const { storeID, ...queryParams } = params
    const query = buildQueryParams(queryParams)
    const response = await api
      .get<ApiResponse<EmployeeData>>(URLs.Employees.list(storeID, query))
      .then((response) => response?.data?.data)

    return {
      data: response.employees,
      pageInfo: {
        first: response.first,
        prev: response.prev,
        cursor: response.cursor,
        next: response.next,
        last: response.last,
      },
    }
  },
  delete: async (params: { orgID?: string; employeeID?: string }): Promise<void> => {
    await api.delete(URLs.Employees.byID(params.orgID || '', params.employeeID || ''))
  },
  createUserEmployee: (
    orgID: string,
    payload: CreateUserEmployeePayload,
  ): Promise<CreateApiResult<void>> =>
    api.post<void>(URLs.Users.createWithEmployee(orgID), payload),
  update: async (payload: Partial<Employee>) => {
    const { orgID, ...patchData } = payload
    return await api
      .patch(URLs.Employees.byID(orgID || '', payload.employeeID || ''), patchData)
      .then((response) => response.data)
  },
  getRegularHours: async (params: EmployeeRegularHoursParams): Promise<EmployeeRegularHour[]> => {
    const { employeeID, storeID } = params
    return await api
      .get<ApiResponse<EmployeeRegularHour[]>>(URLs.Employees.regularHours(employeeID, storeID))
      .then((response) => response.data.data)
  },
  // The create/update replies don't echo `rest` back (see
  // EmployeeRegularHoursSchema on the backend), so callers should invalidate
  // and re-fetch getRegularHours rather than trust these responses.
  createRegularHours: async (payload: SaveEmployeeRegularHoursPayload): Promise<void> => {
    const { employeeID, storeID, hours } = payload
    await api.post(URLs.Employees.regularHours(employeeID, storeID), { hours })
  },
  updateRegularHours: async (payload: UpdateEmployeeRegularHoursPayload): Promise<void> => {
    const { employeeID, storeID, weekDay, hours } = payload
    await api.patch(URLs.Employees.regularHoursByWeekDay(employeeID, storeID, weekDay), { hours })
  },
  deleteRegularHours: async (payload: DeleteEmployeeRegularHoursPayload): Promise<void> => {
    const { employeeID, storeID, weekDays } = payload
    await api.delete(URLs.Employees.regularHours(employeeID, storeID, buildWeekDaysQuery(weekDays)))
  },
}

export const employeesApi: typeof employeesApiReal = __IS_MOCK_MODE__
  ? employeesApiMock
  : employeesApiReal
